import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Users, Feather, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import site from "@/data/site";

function PageHeader() {
  return (
    <section className="relative bg-bloom overflow-hidden">
      <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-blush blur-3xl opacity-60" aria-hidden="true" />
      <div className="container-luxe relative py-20 lg:py-28 text-center">
        <Reveal>
          <span className="eyebrow">Our Story</span>
          <h1 className="display-xl font-display mt-4">
            Soft hearts.
            <span className="block font-script italic text-rose-ink leading-[1.05] mt-1">Beautiful goodbyes.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-ink max-w-2xl mx-auto leading-relaxed">
            Eternal Care exists to bring color, warmth and quiet luxury to the
            most tender moments in life.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Story() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative rounded-[1.75rem] overflow-hidden img-zoom shadow-[0_30px_80px_-25px_rgba(42,34,38,0.3)]">
            <img
              src={site.images.flowers3}
              alt="Delicate pink carnations and white memorial blooms"
              className="w-full h-full object-cover aspect-[4/5]"
              loading="lazy"
            />
          </div>
        </Reveal>

        <Reveal className="lg:col-span-6">
          <span className="eyebrow">Why we exist</span>
          <h2 className="display-lg font-display mt-3">Compassion, in living color.</h2>
          <p className="mt-6 text-muted-ink text-lg leading-relaxed">
            We believe goodbye doesn't have to feel cold. Eternal Care was built
            for the families who want their loved one's last ride to feel
            unmistakably theirs, soft, dignified, and full of grace.
          </p>
          <p className="mt-4 text-muted-ink text-lg leading-relaxed">
            Our signature pink hearse and elegantly coordinated drivers are more
            than a visual, they're a quiet act of love. A promise that the
            person being honored will be remembered exactly the way they lived:
            beautifully.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {[
              { i: Heart, t: "Compassionate", b: "Care from the first call to the final mile." },
              { i: Sparkles, t: "Personal", b: "Tailored to your loved one's spirit." },
              { i: Users, t: "Family-First", b: "Quiet, attentive, never intrusive." },
              { i: Feather, t: "Elegant", b: "Modern luxury, gentle and tasteful." },
            ].map((v, i) => {
              const Icon = v.i;
              return (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-10 h-10 rounded-full bg-blush grid place-items-center shrink-0">
                    <Icon className="w-4 h-4 text-rose-ink" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="font-display text-xl leading-tight">{v.t}</h3>
                    <p className="text-sm text-muted-ink mt-1">{v.b}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PinkAttire() {
  return (
    <section className="section bg-soft-blush relative overflow-hidden">
      <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <span className="eyebrow">Our drivers</span>
          <h2 className="display-lg font-display mt-3">Dressed with intention.</h2>
          <p className="mt-6 text-muted-ink text-lg leading-relaxed">
            Our drivers wear pink suits and coordinated professional attire, a
            deliberate, beautiful detail that softens an emotional day. Every
            uniform is tailored, polished, and chosen to feel respectful and
            warm at once.
          </p>
          <ul className="mt-6 space-y-3 text-muted-ink">
            {["Pink suits & dress suits", "Elegant coordinated attire", "Tasteful, dignified presence", "Trained in family-first care"].map((x) => (
              <li key={x} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-rose" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative rounded-[1.75rem] overflow-hidden img-zoom shadow-[0_30px_80px_-25px_rgba(201,122,136,0.45)]">
            <img src={site.images.hero} alt="Driver wearing a pink suit with Eternal Care card" className="w-full h-full object-cover aspect-[4/5]" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Promise() {
  return (
    <section className="relative bg-charcoal text-white overflow-hidden">
      <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full bg-rose/30 blur-3xl" aria-hidden="true" />
      <div className="container-luxe relative py-20 lg:py-28 text-center">
        <Reveal>
          <p className="font-script text-rose-soft text-3xl">Our quiet promise</p>
          <p className="font-display display-md italic mt-4 max-w-3xl mx-auto leading-snug">
            "Providing dignified, personalized and heartfelt transportation services."
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-pill btn-primary" data-testid="about-cta">
              Reach Out To Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <Seo
        title="About Eternal Care | The Pink Hearse Story | Houston, TX"
        description="The story behind Eternal Care, a luxury pink hearse funeral transportation service celebrating individuality with dignity, warmth and quiet luxury in Houston, Texas."
        path="/about"
      />
      <PageHeader />
      <Story />
      <PinkAttire />
      <Promise />
    </>
  );
}
